import { useState } from "react";
import { useForm } from "react-hook-form";
import { Lock } from "lucide-react";
import * as accountApi from "../../../api/account/api";
import { AuthStatus } from "../../../types/domain";
import { CapsLockWarning } from "../../../components/common/CapsLockWarning";
import { PasswordVisibilityToggle } from "../../../components/common/PasswordVisibilityToggle";
import { useApiError } from "../hooks/useApiError";
import { useCapsLock } from "../../../hooks/useCapsLock";
import { usePasswordVisibility } from "../../../hooks/usePasswordVisibility";
import { mustMatch, PASSWORD_MAX_INPUT_LENGTH, validateNewPassword, validatePasswordLength } from "../utils/validators";
import { ErrorAlert } from "../components/ErrorAlert";
import { SubmitButton } from "../components/SubmitButton";
import { LoginFormField } from "../components/LoginFormField";
import { PageLoader } from "../components/PageLoader";
import { FormOverlay } from "@/components/common/Loading";
import { safeRedirectPath } from "../utils/validators";
import { ViewHeading } from "../components/ViewHeading";

type Props = {
  passwordDays?: string;
  passwordStatus?: string;
};

const getPasswordWarning = (status?: string, days?: string): string | null => {
  const d = parseInt(days ?? "0", 10);
  if (status === "expiring") return `Your password will expire in ${d} day${d === 1 ? "" : "s"}. You can update it now, or choose to continue for now and update it later.`;
  if (status === "expired") return "Your password has expired. Please set a new password to continue.";
  return null;
};

type UpdatePasswordFormValues = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export const UpdatePasswordView = ({ passwordDays, passwordStatus }: Props) => {
  const passwordWarning = getPasswordWarning(passwordStatus, passwordDays);
  const { apiError, setApiError, clearError } = useApiError();
  const { capsLockOn, onKeyDown: onCapsKeyDown, onKeyUp: onCapsKeyUp } = useCapsLock();
  const { showPass, toggle: toggleShowPass } = usePasswordVisibility();
  const [redirecting, setRedirecting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<UpdatePasswordFormValues>();

  const newPassword = watch("newPassword");

  const onSubmit = async (data: UpdatePasswordFormValues) => {
    clearError();
    try {
      const result = await accountApi.updatePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });

      switch (result.status) {
        case AuthStatus.Success:
          setRedirecting(true);
          window.location.href = safeRedirectPath(result.redirectUrl);
          break;
        case AuthStatus.Error:
        default:
          setApiError(result.errorMessage ?? "An error occurred. Please try again.");
      }
    } catch {
      setApiError("An unexpected error occurred. Please try again.");
    }
  };

  return (
    <>
      {redirecting && <PageLoader />}

      <FormOverlay loading={isSubmitting || redirecting}>

      <ViewHeading title="Update Password" />

      {passwordWarning && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 text-[0.8125rem]">
          {passwordWarning}
        </div>
      )}

      {apiError && <ErrorAlert message={apiError} />}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <LoginFormField
          label="Current password"
          type={showPass ? "text" : "password"}
          placeholder="Current password"
          autoComplete="current-password"
          error={errors.currentPassword?.message}
          disabled={isSubmitting}
          icon={<Lock size={16} color="#9CA3AF" strokeWidth={2} />}
          onKeyDown={onCapsKeyDown}
          onKeyUp={onCapsKeyUp}
          trailingElement={<PasswordVisibilityToggle showPass={showPass} onToggle={toggleShowPass} />}
          maxLength={PASSWORD_MAX_INPUT_LENGTH}
          {...register("currentPassword", {
            required: "Current password is required",
            validate: (value) => validatePasswordLength(value),
          })}
        />

        <LoginFormField
          label="New password"
          type={showPass ? "text" : "password"}
          placeholder="New password"
          autoComplete="new-password"
          error={errors.newPassword?.message}
          disabled={isSubmitting}
          icon={<Lock size={16} color="#9CA3AF" strokeWidth={2} />}
          onKeyDown={onCapsKeyDown}
          onKeyUp={onCapsKeyUp}
          trailingElement={<PasswordVisibilityToggle showPass={showPass} onToggle={toggleShowPass} />}
          maxLength={PASSWORD_MAX_INPUT_LENGTH}
          {...register("newPassword", {
            required: "New password is required",
            validate: validateNewPassword,
          })}
        />

        <LoginFormField
          label="Confirm new password"
          type={showPass ? "text" : "password"}
          placeholder="Confirm new password"
          autoComplete="new-password"
          error={errors.confirmPassword?.message}
          disabled={isSubmitting}
          icon={<Lock size={16} color="#9CA3AF" strokeWidth={2} />}
          onKeyDown={onCapsKeyDown}
          onKeyUp={onCapsKeyUp}
          trailingElement={<PasswordVisibilityToggle showPass={showPass} onToggle={toggleShowPass} />}
          maxLength={PASSWORD_MAX_INPUT_LENGTH}
          {...register("confirmPassword", {
            required: "Please confirm your new password",
            validate: {
              length: (value) => validatePasswordLength(value),
              matches: mustMatch(newPassword),
            },
          })}
        />

        <CapsLockWarning visible={capsLockOn} />

        <div className="mb-3">
          <SubmitButton loading={isSubmitting}>Change Password</SubmitButton>
        </div>
      </form>

      <div className="flex gap-3 mt-2">
        {passwordStatus === "expiring" && (
          <button
            type="button"
            onClick={() => {
              setRedirecting(true);
              window.location.href = safeRedirectPath(
                new URLSearchParams(window.location.search).get("ReturnUrl"),
                "/MyHome.aspx?Login=true"
              );
            }}
            className="flex-1 py-[0.7rem] rounded-lg border border-[#E5E7EB] bg-white text-[#374151] text-[0.875rem] font-medium cursor-pointer transition-colors hover:bg-[#F3F4F6]"
          >
            Continue
          </button>
        )}
        <button
          type="button"
          onClick={() => (window.location.href = "/Home/Logout")}
          className="flex-1 py-[0.7rem] rounded-lg border border-[#E5E7EB] bg-white text-[#374151] text-[0.875rem] font-medium cursor-pointer transition-colors hover:bg-[#F3F4F6]"
        >
          Logout
        </button>
      </div>
      </FormOverlay>
    </>
  );
};
